import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Dropdown, Modal } from "react-bootstrap";
import { FirstDayOfWeek, toMonthWindow } from "../../../lib/datetime";
import { useI18n } from "../../../lib/i18n/i18n_provider";
import { Icon } from "../../../components";
import {
  formatDayRange,
  formatMeetingRoomDateRange,
} from "../../../offering/format_date_range";
import { parseTime, Time, TimeInterval } from "../../../offering/time_utils";
import { useAnalytics } from "../../../lib/analytics/analytics";
import { BookingType, PricingType, Space } from "../../../data";
import {
  DateTimePicker,
  DailyValue,
  HourlyValue,
  DateTimeValue,
  isCompleteValue,
  isHourlyValue,
} from "./date_time_picker";
import { parseISO } from "date-fns";
import { useMeetingRoomDailyPricingFeatureFlag } from "../../../core/splitio";
import { AvailableDay, useGetSpaceAvailableDays } from "../../../offering/api";
import classNames from "classnames";
import { PickingRange } from "../date_range_picker/date_range_picker";
import { toDay } from "@/offering/day_utils";
import { useMediaQuery } from "../../../hooks";
import { PiCaretDownLight } from "react-icons/pi";
import { RecurrenceRule } from "@/offering/recurrence";
import {
  anchorOf,
  toRecurringAvailabilityRequest,
  useRecurringSelection,
} from "@/offering_v2/hooks/use_recurring_selection";
import {
  committedRepeatLine,
  presetLabel,
  reviewSubline,
  skippedNote,
  summaryDetail,
  summaryHeadline,
} from "./recurring_booking_copy";
import { RecurringDatesList } from "./recurring_dates_list";
import {
  paneStatus,
  RecurringRepeatPane,
  RepeatToggleRow,
} from "./recurring_repeat_pane";

export interface TimeSlot {
  startTime: string;
  endTime: string[];
  hasBetterRate?: boolean;
  hasHigherRate?: boolean;
  price?: number;
}

interface CheckoutDateTimePickerProps<T> {
  value?: T;
  // The second argument is the repeat rule the customer saved with these
  // dates; undefined when the booking is not (or no longer) recurring.
  onComplete: (value?: DateTimeValue, recurrence?: RecurrenceRule) => void;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  onSelectDate: (date: Date) => void;
  firstDayOfWeek?: FirstDayOfWeek;
  timeslots: TimeSlot[];
  // Whether the slot request for the selected date is still in flight, so the
  // picker can tell a day with no bookable slots apart from one still loading.
  timeslotsLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  availableDays?: AvailableDay[];
  space?: Space | null;
  pickingRange: PickingRange;
  // Fired on every Save with the repeat rule it committed, or undefined for a
  // one-date booking, so the parent can decide what Save leads to.
  onSave?: (recurrence?: RecurrenceRule) => void;
  onToggle?: (isOpen: boolean) => void;
  // Override the inline daily/hourly switch visibility. The default
  // computation gates on `useMeetingRoomDailyPricingFeatureFlag`, which is
  // irrelevant outside meeting rooms; day-office parents pass an explicit
  // value derived purely from pricing presence.
  hideModeSwitch?: boolean;
  // The selected meeting-room layout, so the calendar's per-day "from" prices
  // reflect that layout's rate instead of the space-level base. Event spaces
  // already thread this through the wizard; meeting rooms now do too.
  spaceLayoutId?: string;
  // The committed repeat rule, when the customer already made this booking
  // recurring. Seeds the Repeat control and the second line of the toggle.
  recurrence?: RecurrenceRule;
  // Whether the room may be sold as a recurring booking — the booking policy's
  // `recurring_eligible`, all four gates folded in. The Repeat control renders
  // only when true, so there is no disabled state to explain.
  recurringEligible?: boolean;
}

// Initial Daily/Hourly tab for the picker. Open into Hourly when the value is
// already hourly OR the space is hourly-only (no daily price): an hourly-only
// day pass / day office hides the mode switch, so a Daily default would strand
// the user on a tab with no time picker (sc-48843). Guarded on hasHourlyPrice so
// daily-only and unpriced spaces stay on Daily.
export function initialPickerMode(
  value: DateTimeValue | undefined,
  hasHourlyPrice: boolean,
  hasDailyPrice: boolean
): BookingType {
  return (isHourlyValue(value) || !hasDailyPrice) && hasHourlyPrice
    ? BookingType.Hourly
    : BookingType.Daily;
}

// What a customer who turns Repeat on sees first. Clamped to the horizon by
// the expansion, so a room that cannot hold ten shows the most it can.
const DEFAULT_REPEAT_COUNT = 10;
const DEFAULT_RULE: RecurrenceRule = {
  interval: 1,
  count: DEFAULT_REPEAT_COUNT,
};

// The dates pane that opens beside the calendar on desktop.
const REPEAT_PANE_WIDTH = 340;

// What the calendar needs to lay out seven columns, and the width the popover
// takes when Repeat is off. The measured row is a floor, not a cap: a checkout
// box narrower than this (304px on a standard details page) would otherwise pin
// the calendar below its own minimum, and the seventh column spills into the
// pane beside it.
const CALENDAR_MIN_WIDTH = 350;

// Whether a draft still matches what the parent holds; a withheld pick does not.
function sameValue(a?: DateTimeValue, b?: DateTimeValue): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// A recurring daily booking is one day per date, so a committed span collapses
// to its first day the moment Repeat turns on.
function singleDay(value?: DateTimeValue): DateTimeValue | undefined {
  if (!value || isHourlyValue(value) || !value.startDate) return value;
  return { startDate: value.startDate, endDate: value.startDate };
}

export function CheckoutDateTimePicker<T extends DateTimeValue>(
  props: CheckoutDateTimePickerProps<T>
) {
  const {
    value,
    onComplete,
    onNextVisibleDates,
    onSelectDate,
    timeslots,
    timeslotsLoading,
    disabled,
    placeholder,
    availableDays,
    space,
    pickingRange,
    onSave,
    onToggle,
    hideModeSwitch,
    firstDayOfWeek,
    spaceLayoutId,
    recurrence,
    recurringEligible,
  } = props;

  const { device } = useMediaQuery();

  const meetingRoomDailPricingFeatureFlag =
    useMeetingRoomDailyPricingFeatureFlag(space?.partnerID || "");

  const { fbt, locale } = useI18n();
  const analytics = useAnalytics();

  let hasDailyPrice = false;
  let hasHourlyPrice = false;
  space?.pricings.forEach((p) => {
    if (p.type === PricingType.Daily) {
      hasDailyPrice = true;
    } else if (p.type === PricingType.Hourly) {
      hasHourlyPrice = true;
    }
  });

  const [show, setShow] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<DateTimeValue>();
  const [switchValue, setSwitchValue] = useState(
    initialPickerMode(value, hasHourlyPrice, hasDailyPrice)
  );

  // The repeat rule is draft state: it is committed with the dates on Save and
  // never before, because the committed value re-quotes on every change and a
  // recurring quote rebuilds every date server-side. While Repeat is on, the
  // dates themselves are held back too, so one Save produces one quote.
  const [repeatOn, setRepeatOn] = useState(!!recurrence);
  const [draftRule, setDraftRule] = useState<RecurrenceRule>(
    recurrence ?? DEFAULT_RULE
  );
  const [sheetStep, setSheetStep] = useState<"date" | "review">("date");
  // The popover's natural width — the width of the picker row it drops from,
  // which is what the menu takes when Repeat is off. Measured whenever the
  // picker opens or Repeat turns on (both are events, both happen with the row
  // mounted), so a URL that arrives with a saved rule opens already wide.
  // The calendar column is pinned to it so the calendar and time pickers keep
  // their size, and the dates pane is added beside them.
  const rowRef = useRef<HTMLDivElement>(null);
  const [calendarWidth, setCalendarWidth] = useState<number>();
  const measureCalendarWidth = () => {
    const width = rowRef.current?.getBoundingClientRect().width;
    if (width) setCalendarWidth(width);
  };
  // The column the calendar is pinned to once Repeat widens the popover.
  const calendarColumnWidth = calendarWidth
    ? Math.max(calendarWidth, CALENDAR_MIN_WIDTH)
    : undefined;

  const localMonthWindow =
    !isHourlyValue(localValue) && localValue?.startDate && localValue.endDate
      ? toMonthWindow(localValue.startDate, localValue.endDate)
      : undefined;

  const {
    data: { availableDays: localAvailableDates },
  } = useGetSpaceAvailableDays(
    space?.id,
    localMonthWindow
      ? {
          startDate: toDay(localMonthWindow.start),
          endDate: toDay(localMonthWindow.end),
        }
      : undefined
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // The rule passed here is `repeatOn`, not `repeatActive`, on purpose: while
  // the horizon says the room cannot repeat, the switch stays on underneath the
  // hidden control so the refused request keeps its key — and its answer.
  // Deriving the refusal from a key that went away would bring the control
  // straight back.
  const draft = useRecurringSelection(
    space?.id,
    localValue,
    repeatOn ? draftRule : undefined
  );
  const committed = useRecurringSelection(space?.id, value, recurrence);

  // The policy said eligible but the availability endpoint disagreed (the flag
  // flipped between page load and now): drop the control rather than offer a
  // recurring booking the quote would refuse.
  const showRepeat = !!recurringEligible && !!space?.id && !draft.ineligible;
  const repeatActive = showRepeat && repeatOn;
  // Under the desktop breakpoint the picker becomes a full-height sheet — but
  // only where it can repeat. The two other callers of this picker keep the
  // popover they have always had.
  const useSheet = showRepeat && device !== "desktop";

  const status = paneStatus({
    hasWindow: !!toRecurringAvailabilityRequest(localValue),
    loading: draft.loading,
    error: !!draft.error,
    expansion: draft.expansion,
  });
  const draftReady = repeatActive && status === "ready" && !!draft.expansion;

  const handleHourlyChange = useCallback(
    (value: HourlyValue) => {
      if (value.date) {
        onSelectDate(parseISO(value.date));
        setLocalValue({ date: value.date });

        analytics.event(
          "Date Select Click",
          {
            Date: value.date,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }
      if (value.startTime) {
        setLocalValue({
          ...localValue,
          startTime: value.startTime,
          endTime: value.endTime,
        });
        if (value.endTime && !repeatActive) {
          onComplete({
            date: isHourlyValue(localValue) ? localValue.date : undefined,
            startTime: value.startTime,
            endTime: value.endTime,
          });
        }
      }
    },
    [onSelectDate, analytics, onComplete, localValue, repeatActive]
  );

  const handleDailyChange = useCallback(
    (value: DailyValue) => {
      if (value.startDate) {
        onSelectDate(parseISO(value.startDate));

        analytics.event(
          "Date Select Click",
          {
            Date: value.startDate,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }

      // A recurring daily booking is one day per date, so the range collapses
      // to the day picked and the draft carries both ends.
      setLocalValue(
        repeatActive
          ? { startDate: value.startDate, endDate: value.startDate }
          : { startDate: value.startDate }
      );

      if (value.endDate && !repeatActive) {
        onComplete({
          startDate: value.startDate,
          endDate: value.endDate,
        });
      }
    },
    [onSelectDate, analytics, onComplete, repeatActive]
  );

  // The repeat draft is seeded from the saved rule each time the picker opens
  // and discarded each time it closes without a Save. Nothing else can change
  // the saved rule while the picker is open — Save and Repeat-off are the only
  // writers and both close it — so there is no prop to keep in sync.
  const seedDraft = () => {
    measureCalendarWidth();
    setRepeatOn(!!recurrence);
    setDraftRule(recurrence ?? DEFAULT_RULE);
    setSheetStep("date");
  };

  // Back to whatever was last saved. A draft withheld while Repeat was on is
  // discarded; an ordinary selection was committed as it was made and stays.
  const resetDraft = () => {
    if (repeatOn) setLocalValue(value);
    seedDraft();
  };

  const close = () => {
    setShow(false);
    setSheetStep("date");
    onToggle?.(false);
  };

  const handleSave = () => {
    let saved: RecurrenceRule | undefined;
    if (draftReady && draft.expansion) {
      saved = {
        interval: draftRule.interval,
        // The count the customer will actually get, not the one they stepped
        // to, so the URL and the quote agree with the picker.
        count: draft.expansion.booked.length,
      };
      onComplete(localValue, saved);
    } else if (
      repeatOn &&
      !repeatActive &&
      isCompleteValue(localValue) &&
      !sameValue(localValue, value)
    ) {
      // The switch is on but the horizon refused the room after a pick was
      // withheld for it: the pick is still a complete ordinary booking, so
      // Save keeps it rather than dropping it for the dates saved before.
      // Only a pick that differs from the saved one — with the switch on from
      // the URL while the policy is still loading, picks commit as they are
      // made and the saved rule must not be cleared underneath them.
      onComplete(localValue, undefined);
    }
    close();
    onSave?.(saved);

    analytics.event(
      "Save Date Click",
      {
        "Booking Start Date": isHourlyValue(localValue)
          ? localValue?.date
          : localValue?.startDate,
        "Booking End Date": isHourlyValue(localValue)
          ? localValue?.date
          : localValue?.endDate,
        "Booking Start Time": isHourlyValue(localValue)
          ? localValue?.startTime
          : undefined,
        "Booking End Time": isHourlyValue(localValue)
          ? localValue?.endTime
          : undefined,
        Repeat: repeatActive
          ? `${draftRule.interval}:${draft.expansion?.booked.length ?? 0}`
          : undefined,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  };

  const handleCancel = () => {
    resetDraft();
    close();
  };

  const durationUnit: "hour" | "day" =
    switchValue === BookingType.Daily ? "day" : "hour";

  // Hourly repeats a window, so it needs the date and both times; daily repeats
  // a day, so the first date is enough (a span collapses to it on toggle).
  // Keyed on the mode, not the value's shape: an hourly picker can hold a
  // daily-shaped value from the URL, and that has no window to repeat.
  const canRepeat =
    durationUnit === "hour"
      ? isHourlyValue(localValue) && isCompleteValue(localValue)
      : !!localValue && !isHourlyValue(localValue) && !!localValue.startDate;

  // Off and nothing to repeat yet: the switch is disabled. On, it always
  // works, so losing the window never traps the user with Repeat on.
  const repeatDisabled = !canRepeat && !repeatOn;

  const repeatSectionRef = useRef<HTMLDivElement>(null);

  const handleToggleRepeat = () => {
    if (repeatDisabled) return;
    if (repeatOn) {
      setRepeatOn(false);
      setSheetStep("date");
      // Turning Repeat off is the one edit that commits immediately: the dates
      // already picked go back to being an ordinary booking, as they would
      // have been had the switch never been touched. With the draft window
      // incomplete, the saved dates carry the cleared rule instead — otherwise
      // the parent stays recurring and reopening flips the switch back on.
      if (isCompleteValue(localValue)) onComplete(localValue, undefined);
      else if (isCompleteValue(value)) onComplete(value, undefined);
      return;
    }
    measureCalendarWidth();
    const turnOn = () => {
      setLocalValue(singleDay(localValue));
      setRepeatOn(true);
    };
    if (!useSheet) {
      turnOn();
      return;
    }
    // In the sheet the repeat controls mount under the switch, which sits
    // below the calendar and time pickers — usually at the bottom of the
    // viewport, so they would land out of sight. Render them now, then scroll
    // them into view. Opening the sheet with a saved rule does not scroll, so
    // the calendar stays where the customer expects to find it. One scroll is
    // enough: the inline pane keeps its shape from the loading state to the
    // ready one, so nothing appears below the fold once the horizon answers.
    flushSync(turnOn);
    repeatSectionRef.current?.scrollIntoView({
      block: "start",
      behavior: "auto",
    });
  };

  const handleSwitch = (value: BookingType) => {
    setSwitchValue(value);
    if (isHourlyValue(localValue)) {
      onComplete({ date: undefined });
    } else {
      onComplete({ startDate: undefined, endDate: undefined });
    }
  };

  const markedDates = draftReady
    ? draft.expansion!.booked.map((d) => parseISO(d))
    : undefined;

  const baseProps = {
    switchValue,
    space,
    spaceLayoutId,
    timeslots,
    timeslotsLoading,
    availableDays,
    firstDayOfWeek,
    markedDates,
    disableSelectPastMonth: true,
    onChangeMonth: onNextVisibleDates,
    onSwitch: handleSwitch,
    hideSwitch:
      hideModeSwitch ??
      (!(hasDailyPrice && hasHourlyPrice) ||
        !meetingRoomDailPricingFeatureFlag),
  };

  const calendarComponent: JSX.Element =
    switchValue === BookingType.Daily ? (
      <DateTimePicker<DailyValue>
        {...baseProps}
        value={!isHourlyValue(localValue) ? localValue : undefined}
        onChange={handleDailyChange}
        // A recurring daily booking is one day per date: no spans to pick.
        pickingRange={repeatActive ? PickingRange.OneDate : pickingRange}
      />
    ) : (
      <DateTimePicker<HourlyValue>
        {...baseProps}
        value={isHourlyValue(localValue) ? localValue : undefined}
        onChange={handleHourlyChange}
        pickingRange={PickingRange.OneDate}
      />
    );

  const hourlySelectedDateString =
    isHourlyValue(value) &&
    value.date &&
    formatMeetingRoomDateRange(value, fbt, locale, "");

  const dailySelectedDateString =
    !isHourlyValue(localValue) &&
    localValue?.startDate &&
    localValue.endDate &&
    formatDayRange(
      {
        start: localValue.startDate,
        end: localValue.endDate,
      },
      fbt,
      locale,
      "",
      localAvailableDates
        ?.filter((d) => d.availableFullDay)
        .map((d) => new Date(d.date))
    );

  const selectedDateString =
    hourlySelectedDateString || dailySelectedDateString;

  const committedRepeatString =
    recurrence && committed.expansion
      ? committedRepeatLine(committed.expansion, fbt)
      : undefined;

  const isSaveButtonDisabled = repeatActive
    ? !draftReady
    : isHourlyValue(value)
    ? !(value?.date && value.startTime && value.endTime)
    : !(value?.startDate && value.endDate);

  const anchor = anchorOf(localValue);
  const repeatToggleRow = showRepeat ? (
    <RepeatToggleRow
      on={repeatOn}
      onToggle={handleToggleRepeat}
      anchor={anchor}
      durationUnit={durationUnit}
      disabled={repeatDisabled}
    />
  ) : null;

  const repeatPane = (variant: "pane" | "inline") =>
    repeatActive ? (
      <RecurringRepeatPane
        rule={draftRule}
        onChangeRule={setDraftRule}
        expansion={draft.expansion}
        status={status}
        variant={variant}
        durationUnit={durationUnit}
        onReview={() => setSheetStep("review")}
      />
    ) : null;

  // The footer's one line about the draft: the recurring summary while Repeat
  // is on, the saved date otherwise.
  const footerSummary =
    repeatActive && draft.expansion && status === "ready" ? (
      <div>
        <div className="fw-bold" data-testid="repeat-summary">
          {summaryHeadline(draft.expansion, fbt)}
        </div>
        <div className="small text-muted">
          {summaryDetail(draft.expansion, fbt)}
        </div>
      </div>
    ) : repeatActive ? null : selectedDateString ? (
      <div>{selectedDateString}</div>
    ) : null;

  const saveButton = (
    <button
      disabled={isSaveButtonDisabled}
      className="btn btn-primary"
      onClick={handleSave}
      data-testid="daily-hourly-save-button-testId"
    >
      {fbt("Save")}
    </button>
  );

  const reviewTimeSuffix =
    isHourlyValue(localValue) && localValue.startTime && localValue.endTime
      ? ` · ${fbt.formatTime(
          parseTime(localValue.startTime)
        )} → ${fbt.formatTime(parseTime(localValue.endTime))}`
      : "";

  const reviewScreen = draft.expansion ? (
    <div data-testid="recurring-review">
      <div className="px-3 pt-3 pb-2 border-bottom">
        <div>
          {presetLabel(draftRule.interval, fbt)}
          {reviewTimeSuffix}
        </div>
        <div className="small text-muted">
          {reviewSubline(draft.expansion, fbt)}
        </div>
      </div>
      <div className="px-3">
        <RecurringDatesList expansion={draft.expansion} groupByMonth />
      </div>
      <div className="p-3 small text-muted">
        {skippedNote(draft.expansion, fbt)}
      </div>
    </div>
  ) : null;

  const sheetTitle =
    sheetStep === "review"
      ? fbt("Your recurring booking")
      : repeatOn
      ? fbt("Dates and repeat")
      : fbt("Choose a date");

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        ref={rowRef}
        className={`checkout-box-picker row ${disabled ? "disabled" : ""}`}
        id={`checkout-datetime-picker`}
        show={show && !useSheet}
        autoClose="outside"
        onToggle={(isOpen) => {
          if (disabled) return;
          // Dismissing the popover (outside click, Escape) commits nothing, so
          // whatever Repeat had withheld is discarded with it.
          if (isOpen) seedDraft();
          else resetDraft();
          setShow(isOpen);
          onToggle?.(isOpen);
        }}
      >
        <Dropdown.Toggle data-testid="date-selection">
          <div className="d-flex align-items-center justify-content-between text-align-left">
            <div style={{ width: "85%" }} className="overflow-hidden">
              <div style={{ fontSize: 12 }} className="fw-bold text-uppercase">
                {placeholder ? fbt(placeholder) : fbt("Date")}
              </div>
              <div className="mt-1" style={{ fontSize: 16 }}>
                {selectedDateString ? selectedDateString : fbt("Select date")}
              </div>
              {committedRepeatString && (
                // The toggle inherits Bootstrap's nowrap; this line is the one
                // long enough to need a second row inside the card's width.
                <div
                  className="mt-1 small text-muted text-wrap text-start"
                  data-testid="date-selection-repeat"
                >
                  {committedRepeatString}
                </div>
              )}
            </div>
            <div>
              <PiCaretDownLight size={20} />
            </div>
          </div>
        </Dropdown.Toggle>
        {!useSheet && (
          <Dropdown.Menu
            // The card sits in the page's right column, so the wide menu grows
            // leftwards from the card's edge rather than off the viewport.
            // Flipping the alignment also makes Popper re-place the menu at
            // its new width; the menu itself stays mounted so the calendar
            // keeps the month the user was viewing.
            align={repeatActive ? "end" : "start"}
            popperConfig={{
              modifiers: [
                {
                  name: "preventOverflow",
                  options:
                    device !== "mobile"
                      ? {
                          altAxis: true,
                          padding: { top: 80 },
                        }
                      : undefined,
                },
              ],
            }}
            className="checkout-datetime-picker rounded"
            // Repeat widens the popover with a second pane instead of
            // lengthening it, so the calendar and the dates sit side by side.
            // The dropdown is a Bootstrap `.row`, whose children are capped at
            // `max-width: 100%`; the widened menu has to lift that cap or its
            // columns overflow the white box.
            style={{
              // The two-column layout keeps its floor even when the row could
              // not be measured, so the pane never squeezes the calendar. The
              // floor is the two columns themselves: neither grows, so a wider
              // floor is empty ground past the pane's edge, not a wider pane.
              minWidth: repeatActive
                ? CALENDAR_MIN_WIDTH + REPEAT_PANE_WIDTH
                : CALENDAR_MIN_WIDTH,
              width:
                repeatActive && calendarColumnWidth
                  ? calendarColumnWidth + REPEAT_PANE_WIDTH
                  : undefined,
              maxWidth: repeatActive ? "none" : undefined,
            }}
          >
            <div data-testid="daily-hourly-calendar">
              <div className="d-flex align-items-stretch">
                <div
                  style={{
                    // Without a measurement the calendar still keeps a
                    // basis, so the fixed-width pane cannot squeeze it.
                    flex:
                      repeatActive && calendarColumnWidth
                        ? `0 0 ${calendarColumnWidth}px`
                        : "1 1 400px",
                    minWidth: 0,
                  }}
                >
                  {calendarComponent}
                  {repeatToggleRow}
                </div>
                {repeatActive && (
                  // The calendar column sets the row's height; the pane takes
                  // exactly that (absolutely positioned inside a stretched
                  // column) and scrolls its dates within it, so a long date
                  // list never makes the popover taller than the calendar.
                  <div
                    className="border-start position-relative"
                    style={{ flex: `0 0 ${REPEAT_PANE_WIDTH}px`, minWidth: 0 }}
                  >
                    <div className="position-absolute" style={{ inset: 0 }}>
                      {repeatPane("pane")}
                    </div>
                  </div>
                )}
              </div>
              <div
                className={classNames(
                  "px-3 pb-3 d-flex flex-row-reverse justify-content-between align-items-center",
                  repeatActive && "pt-3 border-top"
                )}
              >
                <div className="d-flex align-items-center gap-3">
                  {repeatActive && (
                    <button
                      type="button"
                      className="btn btn-link text-dark p-0"
                      onClick={handleCancel}
                    >
                      {fbt("Cancel")}
                    </button>
                  )}
                  {saveButton}
                </div>
                {footerSummary}
              </div>
            </div>
          </Dropdown.Menu>
        )}
      </Dropdown>
      {useSheet && (
        <Modal
          show={show}
          onHide={handleCancel}
          fullscreen
          className="checkout-datetime-picker checkout-datetime-picker-sheet header-center"
          data-testid="checkout-datetime-picker-sheet"
        >
          <Modal.Header closeButton>
            <Modal.Title as="h6">{sheetTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {sheetStep === "review" && reviewScreen ? (
              reviewScreen
            ) : (
              <div data-testid="daily-hourly-calendar">
                {calendarComponent}
                <div ref={repeatSectionRef} data-testid="repeat-section">
                  {repeatToggleRow}
                  {repeatPane("inline")}
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between align-items-center">
            <div className="flex-grow-1">{footerSummary}</div>
            <div className="d-flex align-items-center gap-3">
              {sheetStep === "review" ? (
                <button
                  type="button"
                  className="btn btn-link text-dark p-0"
                  onClick={() => setSheetStep("date")}
                >
                  {fbt("Edit")}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-link text-dark p-0"
                  onClick={handleCancel}
                >
                  {fbt("Cancel")}
                </button>
              )}
              {saveButton}
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

interface TimeRangePickerProps {
  timeslots: TimeSlot[];
  value?: Partial<TimeInterval> | null;
  onChangeTime?: (value: Partial<TimeInterval>) => void;
}

export function TimeRangePicker(props: TimeRangePickerProps) {
  const { timeslots, value, onChangeTime } = props;
  const { start, end } = value || {};
  const { fbt } = useI18n();

  const handleStartTimeChange = React.useCallback(
    (time: Time) => {
      if (!onChangeTime) {
        return;
      }

      onChangeTime({
        start: time,
      });
    },
    [onChangeTime]
  );

  const handleEndTimeChange = React.useCallback(
    (time: Time) => {
      if (!onChangeTime) {
        return;
      }

      onChangeTime({
        start,
        end: time,
      });
    },
    [start, onChangeTime]
  );

  const startTimeOptions = timeslots.map((t) => ({
    value: t.startTime,
    label: fbt.formatTime(parseTime(t.startTime)),
    hasBetterRate: t.hasBetterRate,
    hasHigherRate: t.hasHigherRate,
  }));
  const startTimeSlots = timeslots.find((t) => t.startTime === start);

  const endTimeOptions = startTimeSlots
    ? startTimeSlots.endTime.map((t) => ({
        value: t,
        label: fbt.formatTime(parseTime(t)),
      }))
    : [];

  return (
    <>
      <div className="time-range-picker-start">
        <TimePicker
          label={fbt("Start time")}
          onChange={handleStartTimeChange}
          options={startTimeOptions}
          value={start}
          disabled={timeslots?.length < 1}
        />
      </div>
      <div className="time-range-picker-to">
        <span>{fbt("to")}</span>
      </div>
      <div className="time-range-picker-end">
        <TimePicker
          label={fbt("End time")}
          onChange={handleEndTimeChange}
          options={endTimeOptions}
          value={end}
          disabled={!start}
        />
      </div>
    </>
  );
}

interface TimePickerOption {
  label: string;
  value: string;
  hasBetterRate?: boolean;
  hasHigherRate?: boolean;
}

interface TimePickerProps {
  label: string;
  options: TimePickerOption[];
  value?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

function TimePicker(props: TimePickerProps) {
  const { label, value, options, onChange, disabled = false } = props;
  const { fbt } = useI18n();

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        className="checkout-box-picker time-picker"
        id={`checkout-time-picker`}
        data-testid={"checkout-time-picker-testId"}
        drop="down"
        onSelect={(value: any) => onChange(value)}
      >
        <Dropdown.Toggle disabled={disabled}>
          <div className="row">
            <div className="col-10">
              {value ? (
                <span className="checkout-box-picker-selected">
                  {fbt.formatTime(parseTime(value))}
                </span>
              ) : (
                <span>{label}</span>
              )}
            </div>
            <div className="checkout-box-datetime-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="time-picker-v2"
          data-testid="checkout-time-picker-dropdown-menu"
        >
          {options.map((opt) => (
            <Dropdown.Item
              className="time-picker-v2"
              eventKey={opt.value}
              data-testid={"time-picker-v2-testId"}
              active={opt.value === value}
            >
              <span
                className={classNames(
                  "col align-self-start",
                  opt.hasBetterRate
                    ? "better-hour"
                    : opt.hasHigherRate
                    ? "higher-hour"
                    : ""
                )}
              >
                {opt.label}
                {opt.hasBetterRate && (
                  <Icon name="dot" size={6} color="#038231" />
                )}
                {opt.hasHigherRate && (
                  <Icon name="dot" size={6} color="#FF9E18" />
                )}
              </span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
